import { useRef, type KeyboardEvent, type ReactNode } from 'react';

import { cn } from '../lib/cn';
import { useFieldId } from '../lib/useId';

export interface TabItem {
  key: string;
  label: ReactNode;
  /** A count or status shown after the label, e.g. the number of open tasks. */
  badge?: ReactNode;
  disabled?: boolean;
}

export interface TabsProps {
  tabs: TabItem[];
  value: string;
  onChange: (key: string) => void;
  /** Accessible name for the tab list, e.g. "Matter sections". */
  label: string;
  className?: string;
  children?: ReactNode;
}

/**
 * A tab list following the ARIA tabs pattern.
 *
 * Manual activation: arrows MOVE focus, Enter or Space selects. Automatic activation
 * (selecting on arrow) is tempting and wrong here — each tab on the case workspace
 * triggers a network request, and arrowing across seven of them would fire seven.
 *
 * The panel is rendered by the caller. Give it `role="tabpanel"` and
 * `aria-labelledby={tabId(value)}` — or use `TabPanel` below, which does both.
 */
export function Tabs({ tabs, value, onChange, label, className, children }: TabsProps) {
  const baseId = useFieldId();
  const refs = useRef<(HTMLButtonElement | null)[]>([]);

  const enabled = tabs.map((tab, index) => (tab.disabled ? -1 : index)).filter((i) => i >= 0);

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const currentIndex = tabs.findIndex((tab) => tab.key === value);
    const position = enabled.indexOf(currentIndex);
    let next: number | undefined;

    switch (event.key) {
      case 'ArrowRight':
        next = enabled[(position + 1) % enabled.length];
        break;
      case 'ArrowLeft':
        next = enabled[position <= 0 ? enabled.length - 1 : position - 1];
        break;
      case 'Home':
        next = enabled[0];
        break;
      case 'End':
        next = enabled[enabled.length - 1];
        break;
      default:
        return;
    }
    event.preventDefault();
    if (next !== undefined) refs.current[next]?.focus();
  };

  return (
    <div className={className}>
      <div
        role="tablist"
        aria-label={label}
        onKeyDown={onKeyDown}
        className="flex gap-1 overflow-x-auto border-b"
      >
        {tabs.map((tab, index) => {
          const selected = tab.key === value;
          return (
            <button
              key={tab.key}
              ref={(element) => {
                refs.current[index] = element;
              }}
              type="button"
              role="tab"
              id={`${baseId}-tab-${tab.key}`}
              aria-selected={selected}
              aria-controls={`${baseId}-panel-${tab.key}`}
              // Roving tabindex: one stop for the whole tab list, then arrows.
              tabIndex={selected ? 0 : -1}
              disabled={tab.disabled}
              onClick={() => onChange(tab.key)}
              className={cn(
                'relative flex shrink-0 items-center gap-2 whitespace-nowrap px-3 py-2',
                'text-base font-medium transition-colors disabled:opacity-40',
                selected
                  ? 'text-primary after:absolute after:inset-x-0 after:-bottom-px after:h-0.5 after:bg-primary'
                  : 'text-ink-muted hover:text-ink',
              )}
            >
              {tab.label}
              {tab.badge !== undefined && tab.badge !== null && (
                <span className="rounded-full bg-surface-sunken px-1.5 py-0.5 text-xs text-ink-muted">
                  {tab.badge}
                </span>
              )}
            </button>
          );
        })}
      </div>
      {children}
    </div>
  );
}

export interface TabPanelProps {
  /** Must match the `Tabs` instance. Use `useTabIds()` when they are far apart. */
  baseId: string;
  tabKey: string;
  children: ReactNode;
  className?: string;
}

export function TabPanel({ baseId, tabKey, children, className }: TabPanelProps) {
  return (
    <div
      role="tabpanel"
      id={`${baseId}-panel-${tabKey}`}
      aria-labelledby={`${baseId}-tab-${tabKey}`}
      tabIndex={0}
      className={className}
    >
      {children}
    </div>
  );
}
